/**
 * Responsibility: everything about what this card has actually sold for,
 * on one tab.
 *
 * The same tab on both dialogs. Before this, the figures, the grade ladder
 * and the sale list were stacked down one scrolling column and the dialog
 * ran past the bottom of the screen; splitting the sales out is what let
 * both fit without scrolling the page behind them.
 *
 * Ladder beside chart rather than under it. They answer different halves of
 * one question - what each grade is worth, and which way that is moving -
 * and reading them side by side is the point of putting them on a tab
 * together.
 */
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LaunchIcon from '@mui/icons-material/Launch';
import type { GradeQuote, SoldSale } from '../../api/client';
import { GradeLadder } from './GradeLadder';
import { GradePicker } from './GradePicker';
import { SalesChart, SalesTable } from './SalesHistory';
import { bucketsOf, defaultBucket, salesIn } from './saleGrades';

type CardSalesTabProps = {
  grades: GradeQuote[];
  sales: SoldSale[];
  sourceUrl: string | null;
  loading?: boolean;
  /** The grade this page is already about. Opens the list on it. */
  preferredGrade?: string | null;
};

export function CardSalesTab({
  grades,
  sales,
  sourceUrl,
  loading = false,
  preferredGrade = null,
}: CardSalesTabProps) {
  const [grade, setGrade] = useState<string | null>(null);
  const buckets = useMemo(() => bucketsOf(sales), [sales]);
  const bucket = grade ?? defaultBucket(buckets, preferredGrade);
  // Chart and table show the same grade. Two controls, or a table that
  // did not follow the chart, would make the count under the chart mean
  // something different from the rows under it.
  const shown = useMemo(() => salesIn(sales, bucket), [sales, bucket]);

  const nothing = grades.length === 0 && sales.length === 0;

  if (nothing) {
    return (
      <Stack spacing={1} sx={{ py: 3, px: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Looking for completed sales' : 'No sale history for this card'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          No catalogue product matched this card confidently. The matcher would rather find
          nothing than attach another card&apos;s sales to this one.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2.5}
        sx={{ minWidth: 0, flexShrink: 0 }}
      >
        <Stack spacing={0.75} sx={{ flexShrink: 0, minWidth: { md: 260 } }}>
          <Typography variant="subtitle2">Price by grade</Typography>
          <GradeLadder grades={grades} />
        </Stack>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Sale history
          </Typography>
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <GradePicker buckets={buckets} value={bucket} onChange={setGrade} />
            <SalesChart sales={shown} loading={loading} />
          </Stack>
        </Box>
      </Stack>

      <Divider sx={{ flexShrink: 0 }} />

      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2">
          Completed sales{shown.length > 0 ? ` (${shown.length})` : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {sourceUrl ? (
          <Link
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            Source
            <LaunchIcon sx={{ fontSize: 12 }} />
          </Link>
        ) : null}
      </Stack>

      {/* The one part allowed to scroll: a busy card has hundreds of
          sales, and the dialog is not going to grow to fit them. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
        <SalesTable sales={shown} />
      </Box>
    </Stack>
  );
}
